/**
 * MockHistory - user's past AI Mock Interviews.
 *
 * Sprint A: read-only list. Click-through to a consumer result page
 * comes later (we currently only have the recruiter-side review page,
 * which mocks are deliberately filtered out of).
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Clock, ChevronRight, Trash2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../contexts/LanguageContext';
import { showToast } from '../../utils/errorHandler';
import { cleanTemplateTitle as cleanTitle, listMockHistory, deleteMock, type MockHistoryItem } from '../../api/interview';


const MockHistory: React.FC = () => {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const { language: uiLang, toggleLanguage } = useLanguage();
  const [items, setItems] = useState<MockHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await listMockHistory(apiCall, 100);
        if (cancelled) return;
        setItems(r.history);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiCall]);

  const T = uiLang === 'de' ? STRINGS_DE : STRINGS_EN;

  const onDelete = async (sessionId: string) => {
    if (deletingId) return;
    if (!window.confirm(T.deleteConfirm)) return;
    try {
      setDeletingId(sessionId);
      await deleteMock(apiCall, sessionId);
      setItems((prev) => prev.filter((x) => x.session_id !== sessionId));
      showToast(T.deleteSuccess, 'success');
    } catch {
      showToast(T.deleteError, 'error');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between gap-3 mb-6 sm:mb-8">
          <Link
            to="/?tab=practice"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            {T.back}
          </Link>
          <button
            onClick={toggleLanguage}
            className="text-xs font-medium text-slate-500 hover:text-slate-900 px-2 py-1 rounded-full hover:bg-slate-100"
          >
            {uiLang.toUpperCase()}
          </button>
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{T.title}</h1>
        <p className="mt-1 text-sm text-slate-500">{T.subtitle}</p>

        {error && (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-start gap-2 text-sm text-rose-900">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="mt-6">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center text-sm text-slate-500">
              {T.empty}
            </div>
          ) : (
            <ul className="space-y-2 sm:space-y-3">
              {items.map((it) => (
                <HistoryRow
                  key={it.session_id}
                  it={it}
                  uiLang={uiLang}
                  onClick={() => navigate(`/mock/sessions/${it.session_id}`)}
                  onDelete={() => onDelete(it.session_id)}
                  deleting={deletingId === it.session_id}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};


const HistoryRow: React.FC<{
  it: MockHistoryItem;
  uiLang: 'de' | 'en';
  onClick: () => void;
  onDelete: () => void;
  deleting: boolean;
}> = ({ it, uiLang, onClick, onDelete, deleting }) => {
  const T = uiLang === 'de' ? STRINGS_DE : STRINGS_EN;
  // Runs that were dropped before the room opened have no started_at, so fall
  // back to when the run was created -- every row should show when it happened.
  const occurred = new Date((it.started_at ?? it.created_at) * 1000);
  const min = it.duration_seconds != null ? Math.round(it.duration_seconds / 60) : null;

  const statusDisplay = displayStatus(it, T);
  const score = it.overall_score;
  const isAbandoned = (it.ai_confidence === 0 && score == null) || isStaleRunning(it);
  // Mocks have no recruiter and no hire/no-hire judgment, so we never
  // show the recommendation chip here -- it's training feedback only.

  // Only completed mocks have a meaningful detail view (transcript, code,
  // scorecard). Sessions that never got past invite stay non-clickable.
  const isClickable = it.status === 'completed' || (it.status === 'in_progress' && score != null);

  const Content = (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900 truncate">
          {cleanTitle(it.template_title || T.unknownTemplate)}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
          <span>{occurred.toLocaleDateString(uiLang === 'de' ? 'de-DE' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })} {occurred.toLocaleTimeString(uiLang === 'de' ? 'de-DE' : 'en-US', { hour: '2-digit', minute: '2-digit' })}</span>
          {min != null && (
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{min} min</span>
          )}
          {it.language && <span className="uppercase">{it.language}</span>}
          {it.difficulty && <span className="uppercase">{it.difficulty}</span>}
        </div>
      </div>
      <div className="text-right flex-shrink-0 flex items-center gap-2">
        {isAbandoned ? (
          <span className="inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
            {T.abandoned}
          </span>
        ) : score == null && (
          <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            {statusDisplay}
          </span>
        )}
        {isClickable && (
          <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        )}
      </div>
    </div>
  );

  const DeleteBtn = (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onDelete(); }}
      disabled={deleting}
      title={T.deleteLabel}
      className="flex-shrink-0 self-center p-2 rounded-xl text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
    >
      {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
    </button>
  );

  if (!isClickable) {
    return (
      <li className="flex items-stretch gap-1 rounded-3xl border border-slate-200 bg-white px-4 sm:px-5 py-3 sm:py-4 shadow-[0_2px_8px_rgba(15,23,42,0.04)]">
        <div className="min-w-0 flex-1">{Content}</div>
        {DeleteBtn}
      </li>
    );
  }
  return (
    <li className="flex items-stretch gap-1 rounded-3xl border border-slate-200 bg-white px-2 sm:px-3 py-1 shadow-[0_2px_8px_rgba(15,23,42,0.04)] hover:border-pink-200 hover:shadow-[0_4px_18px_-4px_rgba(236,72,153,0.18)] transition-shadow">
      <button
        type="button"
        onClick={onClick}
        className="min-w-0 flex-1 text-left px-2 py-2 sm:py-3"
      >
        {Content}
      </button>
      {DeleteBtn}
    </li>
  );
};


// cleanTitle is imported from the shared helper (cleanTemplateTitle).


// A session the candidate left mid-call keeps status 'in_progress' until the
// backend's abandoned-sweep catches it, which can take hours. It can never be
// resumed (the room shows the interrupted screen, /start answers 409), so past
// this age we stop advertising it as still running.
const STALE_RUNNING_MS = 2 * 60 * 60 * 1000;

function isStaleRunning(it: MockHistoryItem): boolean {
  if (it.status !== 'in_progress' && it.status !== 'started') return false;
  const startedMs = (it.started_at ?? it.created_at) * 1000;
  return Date.now() - startedMs > STALE_RUNNING_MS;
}

function displayStatus(it: MockHistoryItem, T: typeof STRINGS_EN): string {
  if (it.status === 'completed') return T.completed;
  if (it.status === 'in_progress' || it.status === 'started') {
    return isStaleRunning(it) ? T.abandoned : T.inProgress;
  }
  // Pre-start states: the invite exists (and consent may be on file) but the
  // candidate never entered the room.
  if (it.status === 'invited' || it.status === 'account_linked' || it.status === 'consent_given') {
    return T.notStarted;
  }
  if (it.status === 'expired') return T.expired;
  if (it.status === 'cancelled') return T.cancelled;
  // Never leak a raw DB status into the UI.
  return T.unknownStatus;
}


const STRINGS_EN = {
  back: 'Back',
  title: 'Your AI interview simulations',
  subtitle:
    'Every video-interview simulation you have run lives here. Tap to open the recording, transcript and feedback. Recruiters never see these.',
  empty: "You haven't run a simulation yet. Head back to start one.",
  unknownTemplate: 'AI interview simulation',
  completed: 'Scored',
  inProgress: 'In progress',
  notStarted: 'Not started',
  expired: 'Expired',
  abandoned: 'Abandoned',
  cancelled: 'Cancelled',
  unknownStatus: 'Unknown',
  scoreLabel: 'AI score',
  deleteLabel: 'Delete',
  deleteConfirm: 'Delete this simulation permanently (recording, transcript, feedback)? This cannot be undone.',
  deleteSuccess: 'Simulation deleted.',
  deleteError: 'Could not delete. Please try again.',
};

const STRINGS_DE = {
  back: 'Zurück',
  title: 'Deine AI-Interview-Simulationen',
  subtitle:
    'Jede Video-Interview-Simulation, die du gemacht hast, ist hier. Tippe drauf um Aufnahme, Transkript und Feedback zu öffnen. Recruiter:innen sehen das nie.',
  empty: 'Du hast noch keine Simulation gemacht. Geh zurück um zu starten.',
  unknownTemplate: 'AI-Video-Interview',
  completed: 'Bewertet',
  inProgress: 'Läuft',
  notStarted: 'Nicht gestartet',
  expired: 'Abgelaufen',
  abandoned: 'Abgebrochen',
  cancelled: 'Zurückgezogen',
  unknownStatus: 'Unbekannt',
  scoreLabel: 'KI-Score',
  deleteLabel: 'Löschen',
  deleteConfirm: 'Diese Simulation endgültig löschen (Aufnahme, Transkript, Feedback)? Das kann nicht rückgängig gemacht werden.',
  deleteSuccess: 'Simulation gelöscht.',
  deleteError: 'Löschen fehlgeschlagen. Bitte versuche es erneut.',
};


export default MockHistory;
